/**
 * Book Idea Review — coach match, Confidentiality Agreement flow, idea details,
 * document upload, day-chip + slot grid, confirmation. Prototype source:
 * data-page="request". Local state; BFF/CoachRouter/Calendar/Firestore in Phase 3–4.
 * British English.
 */
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { DevPanel } from '../components/DevPanel';
import { NdaModal } from '../components/NdaModal';
import { useSession } from '../context/SessionContext';
import { api, downloadBlob } from '../lib/api';
import { slotLabelToDate } from '../lib/format';
import { loadDevTools, devToolHeaders, type DevToolsState } from '../lib/devTools';
import { useTapToggle } from '../lib/useTapToggle';
import type { DiscloseeDetails } from '../types/ndaText';
import './Book.css';

export const BOOK_CATEGORIES = [
  'Consumer Product',
  'Fashion / Fabric Based',
  'Smart Tech / Connected Products',
  'App / Software',
  'Tabletop / Board Game',
  'Toys / Physical Play',
  'Food / Consumable',
  'Industrial / Agricultural',
  'Home & Garden / DIY',
  'Baby & Child Products',
  'Pet Products',
  'Sports / Fitness / Outdoor Equipment',
  'Health / Medical & Wellness Devices',
  'Beauty / Personal Care',
  'Automotive / Transport',
  'Tools / Hardware',
  'Furniture / Interior Products',
  'Packaging / Retail Display',
  'Educational / Learning Aids',
  'Eco / Sustainability Products',
  'TestToby', // dev/test only — picks Toby King (the project owner)
  'TestJames', // dev/test only — picks James McInerny
  'TestKev', // dev/test only — picks Kevin Cope
  'TestBev', // dev/test only — picks Bev Maloney
] as const;

const SLOTS = ['9.00 am', '10.30 am', '12.00 pm', '1.15 pm', '2.45 pm', '4.15 pm'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function nextWeekdays(count: number): Date[] {
  const days: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let added = 0;
  while (days.length < count) {
    const d = new Date(today);
    d.setDate(today.getDate() + 1 + added);
    days.push(d);
    added++;
  }
  return days;
}

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function longDate(d: Date): string {
  return `${DOW[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  /** True while the file is uploading to the BFF. */
  uploading?: boolean;
  /** Set if the upload failed (shown as an error badge). */
  error?: boolean;
}

export function Book() {
  const navigate = useNavigate();
  const session = useSession();
  const bookDays = useMemo(() => nextWeekdays(6), []);

  const [category, setCategory] = useState('');
  const [coachMatched, setCoachMatched] = useState(false);
  const [matchedCoach, setMatchedCoach] = useState<{ name: string; role: string; bio?: string } | null>(null);
  const [matching, setMatching] = useState(false);
  const [bookFailed, setBookFailed] = useState(false);
  const [confStatus, setConfStatus] = useState<'none' | 'inplace'>('none');
  const [showNdaForm, setShowNdaForm] = useState(false);
  const [signing, setSigning] = useState(false);
  // NDA disclosee form (§10.3) — collected and sent to the BFF for the PDF.
  const [ndaFirst, setNdaFirst] = useState('');
  const [ndaLast, setNdaLast] = useState('');
  const [ndaAddr1, setNdaAddr1] = useState('');
  const [ndaAddr2, setNdaAddr2] = useState('');
  const [ndaTown, setNdaTown] = useState('');
  const [ndaCounty, setNdaCounty] = useState('');
  const [ndaPostcode, setNdaPostcode] = useState('');
  const [ndaCountry, setNdaCountry] = useState('United Kingdom');
  const [ndaError, setNdaError] = useState(false);
  const [ndaGenError, setNdaGenError] = useState(false);
  // NDA modal pop-up state (§10.3 — matches the prototype's .ag-modal).
  const [ndaModalOpen, setNdaModalOpen] = useState(false);
  const [ndaModalIsNew, setNdaModalIsNew] = useState(false);
  const [ndaAgreement, setNdaAgreement] = useState<{
    disclosee: DiscloseeDetails;
    reference: string;
    dateLabel: string;
    signedAt: string;
  } | null>(null);
  const [ideaName, setIdeaName] = useState('');
  const [ideaCustomer, setIdeaCustomer] = useState('');
  const [ideaProblem, setIdeaProblem] = useState('');
  const [ideaSolves, setIdeaSolves] = useState('');
  // Idea-details autosave gating: only PUT after a successful hydration AND a
  // genuine user edit. Prevents the mount-time autosave (or a blur/booking
  // flush) writing empty values over a populated idea doc after a failed load.
  const [ideaHydrated, setIdeaHydrated] = useState(false);
  const [ideaDirty, setIdeaDirty] = useState(false);
  const markIdeaDirty = useCallback(() => setIdeaDirty(true), []);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [phone, setPhone] = useState('');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  // Real availability slots fetched from the BFF after coach match (per-coach
  // review calendar). Each day carries its own slot times. Null = not fetched.
  const [daySlots, setDaySlots] = useState<{ date: string; iso: string; dayLabel: string; slots: { start: string; end: string; label: string }[]; tag?: 'limited' | 'none' }[] | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booked, setBooked] = useState(false);
  // The latest signed NDA agreement id, so the "View agreement" button can
  // download the PDF after signing.
  const [agreementId, setAgreementId] = useState<string | null>(null);
  // Post-booking actions state.
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sendingUpdates, setSendingUpdates] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Dev tools (Ctrl+Shift+K) — simulate failure scenarios.
  // Ctrl+Shift+K avoids Chrome's Ctrl+Shift+D (bookmark tab group) conflict.
  const [devTools, setDevToolsState] = useState<DevToolsState>(() => loadDevTools());
  const [showDevPanel, setShowDevPanel] = useState<boolean>(() => {
    // Only auto-open via the explicit ?dev=1 URL flag. The panel does NOT
    // persist its visibility across reloads — it must be toggled on each
    // session with Ctrl+Shift+K (or ?dev=1), so it never "shows by default".
    const params = new URLSearchParams(window.location.search);
    return params.get('dev') === '1';
  });

  // Keyboard shortcut to reveal the dev panel (Ctrl+Shift+K).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'K' || e.key === 'k')) {
        e.preventDefault();
        // Toggle visibility only — do NOT persist; the panel never auto-opens
        // on reload (only ?dev=1 does that).
        setShowDevPanel((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // §9.4 — mobile/PWA activation: tap the page title 10 times in 6 seconds to
  // toggle the DevPanel. Needed for iOS PWA (no URL bar for ?dev=1) + Android
  // (easier than typing ?dev=1 on a phone keyboard). The hook dispatches a
  // custom event; this listener toggles the panel.
  const onTitleTap = useTapToggle(10, 6000);
  useEffect(() => {
    const handler = () => setShowDevPanel((prev) => !prev);
    window.addEventListener('itk-dev-tap-toggle', handler);
    return () => window.removeEventListener('itk-dev-tap-toggle', handler);
  }, []);

  const updateDevTools = (s: DevToolsState) => setDevToolsState(s);
  const closeDevPanel = () => setShowDevPanel(false);

  // ── Load the active idea's name + details + the latest NDA agreement id + the
  // existing booking from the BFF (so a hard refresh of /book reflects state). ──
  useEffect(() => {
    (async () => {
      // Fetch /me once — used for activeIdeaId, booking, + confidentiality.
      // On a hard refresh of /book, SessionContext is empty (Home hasn't run),
      // so this is the primary hydration source.
      let me: {
        activeIdeaId: string | null;
        booking: { bookingId?: string; coachName: string; coachRole: string; slotLabel: string; status: string } | null;
        confidentialityInPlace: boolean;
      } | null = null;
      if (!session.activeIdeaId || !session.booking) {
        try {
          me = await api.get<{
            activeIdeaId: string | null;
            booking: { bookingId?: string; coachName: string; coachRole: string; slotLabel: string; status: string } | null;
            confidentialityInPlace: boolean;
          }>('/api/v1/me');
        } catch {
          // Non-fatal — fall through with whatever the session has.
        }
      }

      let id = session.activeIdeaId ?? me?.activeIdeaId ?? null;
      if (id && !session.activeIdeaId) session.setActiveIdea(id);
      if (me?.confidentialityInPlace && !session.confidentialityInPlace) {
        session.setConfidentiality(true);
      }
      if (id) {
        api
          .get<{
            id: string;
            name: string;
            ideaDetails?: { customer: string; problem: string; solution: string };
          }>(`/api/v1/ideas/${id}`)
          .then((res) => {
            if (res.name) setIdeaName(res.name);
            if (res.ideaDetails) {
              setIdeaCustomer(res.ideaDetails.customer ?? '');
              setIdeaProblem(res.ideaDetails.problem ?? '');
              setIdeaSolves(res.ideaDetails.solution ?? '');
            }
            setIdeaHydrated(true);
          })
          .catch(() => {
            // Hydration failed — do NOT enable the idea-details autosave, so a
            // failed load cannot result in empty values being PUT over the doc.
          });
        // Hydrate the existing uploads so previously-saved files appear.
        api
          .get<{ uploads: { id: string; name: string; size: number }[] }>(`/api/v1/uploads/${id}`)
          .then((res) => {
            if (res.uploads?.length) setFiles(res.uploads.map((u) => ({ id: u.id, name: u.name, size: u.size })));
          })
          .catch(() => {
            // Non-fatal — no uploads yet or the BFF is unavailable.
          });
      }
      // Load the latest agreement id so "View agreement" works on revisit.
      api
        .get<{
          agreements: {
            id: string;
            createdAt: number;
            reference: string;
            dateLabel: string;
            signedAt: string;
            disclosee: DiscloseeDetails;
          }[];
        }>('/api/v1/agreements')
        .then((res) => {
          const latest = res.agreements?.sort((a, b) => b.createdAt - a.createdAt)[0];
          if (latest) {
            setAgreementId(latest.id);
            // Hydrate the agreement details so the pop-up can render the
            // document inline on "View agreement" without an extra fetch.
            if (latest.disclosee) {
              setNdaAgreement({
                disclosee: latest.disclosee,
                reference: latest.reference,
                dateLabel: latest.dateLabel,
                signedAt: latest.signedAt,
              });
            }
            // Hydrate the confidentiality status so the "View agreement" panel
            // renders on revisit (not the "Request" button again). Fixes the
            // bug where the NDA wasn't retrievable after generation.
            setConfStatus('inplace');
          } else if (session.confidentialityInPlace) {
            // Fallback: the profile flag is set but the agreement list is empty
            // (shouldn't happen, but be defensive).
            setConfStatus('inplace');
          }
        })
        .catch(() => {
          // Fallback to the session flag if the agreement list fetch fails.
          if (session.confidentialityInPlace) setConfStatus('inplace');
        });

      // Hydrate the existing booking so the Book page reflects the confirmed
      // meeting on revisit (not the booking form again). Uses the /me response
      // fetched above (or session.booking if /me wasn't needed).
      const booking = me?.booking ?? session.booking;
      if (booking && (booking.status === 'confirmed' || booking.status === 'rescheduled')) {
        setBooked(true);
        setMatchedCoach({ name: booking.coachName, role: booking.coachRole });
        setCoachMatched(true);
        if (!session.booking) session.setBooking(booking as never);
        // Fetch the full booking record to hydrate the phone number (the
        // session summary doesn't carry it).
        if (booking.bookingId) {
          api
            .get<{ bookings: { id: string; phone?: string; category?: string }[] }>('/api/v1/bookings')
            .then((res) => {
              const full = res.bookings?.find((b) => b.id === booking.bookingId);
              if (full?.phone) setPhone(full.phone);
              if (full?.category) setCategory(full.category);
            })
            .catch(() => {
              // Non-fatal — phone stays as whatever's in state.
            });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced autosave of the idea name + details (customer/problem/solution)
  // to the active idea. Persists so the Book page reflects what the user entered
  // across navigation + reloads. Also saved on blur (below) + booking.
  // Requires a successful hydration AND a genuine user edit so a failed load can
  // never write empty values over the populated idea doc.
  const ideaSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const id = session.activeIdeaId;
    if (!id || !ideaHydrated || !ideaDirty) return;
    if (ideaSaveTimer.current) clearTimeout(ideaSaveTimer.current);
    ideaSaveTimer.current = setTimeout(() => {
      api
        .put(`/api/v1/ideas/${id}`, {
          name: ideaName,
          ideaDetails: { customer: ideaCustomer, problem: ideaProblem, solution: ideaSolves },
        })
        .catch(() => {
          // Non-fatal — the user's input stays in local state.
        });
    }, 800);
    return () => {
      if (ideaSaveTimer.current) clearTimeout(ideaSaveTimer.current);
    };
  }, [ideaName, ideaCustomer, ideaProblem, ideaSolves, ideaHydrated, ideaDirty, session.activeIdeaId]);

  // Save the idea name + details immediately (no debounce) — used on blur + booking.
  // Gated the same way as the debounced autosave: never flush unhydrated/pristine
  // (possibly empty) values over the stored doc.
  const saveIdeaNow = () => {
    const id = session.activeIdeaId;
    if (!id || !ideaHydrated || !ideaDirty) return;
    api
      .put(`/api/v1/ideas/${id}`, {
        name: ideaName,
        ideaDetails: { customer: ideaCustomer, problem: ideaProblem, solution: ideaSolves },
      })
      .catch(() => {
        // Non-fatal.
      });
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  // Open the signed NDA in the in-app pop-up (matching the prototype's .ag-modal).
  // If the agreement details aren't hydrated yet (e.g. on revisit before the
  // mount effect has populated them), fetch the agreement list first. If the
  // inline modal can't be shown (disclosee missing or fetch fails), fall back
  // to downloading the PDF so the user always gets their agreement.
  const viewAgreement = async () => {
    if (!agreementId) return;
    if (ndaAgreement) {
      setNdaModalIsNew(false);
      setNdaModalOpen(true);
      return;
    }
    // Hydrate from the BFF if not already populated.
    try {
      const res = await api.get<{
        agreements: {
          id: string;
          reference: string;
          dateLabel: string;
          signedAt: string;
          disclosee: DiscloseeDetails;
        }[];
      }>('/api/v1/agreements');
      const latest = res.agreements?.sort((a, b) => {
        // createdAt may be present on the doc but isn't typed here — use it if available.
        const ca = (a as unknown as { createdAt?: number }).createdAt ?? 0;
        const cb = (b as unknown as { createdAt?: number }).createdAt ?? 0;
        return cb - ca;
      })[0];
      if (latest && latest.disclosee) {
        setNdaAgreement({
          disclosee: latest.disclosee,
          reference: latest.reference,
          dateLabel: latest.dateLabel,
          signedAt: latest.signedAt,
        });
        setNdaModalIsNew(false);
        setNdaModalOpen(true);
        return;
      }
    } catch {
      // Fall through to the PDF download fallback below.
    }
    // Fallback: if the inline modal can't be shown (disclosee missing or the
    // agreements fetch failed), open the PDF directly so the user always gets
    // their agreement rather than a silent no-op.
    void downloadAgreement();
  };

  // Download the signed NDA PDF (server-side Playwright; auth-protected —
  // downloadBlob attaches the Bearer token).
  const downloadAgreement = async () => {
    if (!agreementId) return;
    try {
      const { blob, filename } = await downloadBlob(`/api/v1/agreements/${agreementId}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? 'confidentiality-agreement.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      showToast("The agreement couldn\u2019t be downloaded right now \u2014 please try again.");
    }
  };

  // Re-send the signed NDA email to the user (§10.3). Best-effort.
  const resendAgreement = async () => {
    if (!agreementId) return;
    try {
      await api.post<{ ok: boolean }>(`/api/v1/agreements/${agreementId}/resend`);
      showToast(`Agreement re-sent to ${session.email ?? 'your email'}`);
    } catch {
      showToast('Could not re-send the email right now — please try again in a moment.');
    }
  };

  const detailsComplete = ideaName.trim().length > 0;
  const bookReady = detailsComplete && phone.trim().length > 0 && selectedDay && selectedSlot;

  const matchCoach = () => {
    if (!category) return;
    setMatching(true);
    setBookFailed(false);
    // BFF: local coach match (instant, no live call). Pass dev-tools headers
    // so the BFF can simulate a coach-match failure if the dev panel requests it.
    api
      .post<{ ok: boolean; status: string; coach: { name: string; role: string; bio?: string } | null }>('/api/v1/bookings/preview', { category, email: session.email ?? '' }, undefined, devToolHeaders(devTools))
      .then((res) => {
        setMatching(false);
        const coach = res.coach ?? { name: 'Thomas Kelly', role: 'Senior innovation coach' };
        setMatchedCoach(coach);
        setCoachMatched(true);
        // Fetch real availability slots for this coach's review calendar (§9.1
        // dedicated-calendar model). Best-effort — falls back to the static
        // SLOTS grid if the fetch fails (keeps the flow working).
        setSlotsLoading(true);
        api
          .get<{ days: { date: string; iso: string; dayLabel: string; slots: { start: string; end: string; label: string }[]; tag?: 'limited' | 'none' }[] }>(`/api/v1/bookings/slots?coachName=${encodeURIComponent(coach.name)}`)
          .then((slotsRes) => {
            setSlotsLoading(false);
            if (slotsRes.days?.length) setDaySlots(slotsRes.days);
          })
          .catch(() => {
            setSlotsLoading(false);
            // Non-fatal — the static SLOTS grid below is the fallback.
          });
      })
      .catch(() => {
        setMatching(false);
        // Match failed — fall back to a default coach so the flow continues.
        setMatchedCoach({ name: 'Thomas Kelly', role: 'Senior innovation coach' });
        setCoachMatched(true);
      });
  };

  const requestNDA = () => setShowNdaForm(true);

  const signNDA = () => {
    // Validate the disclosee fields before sending (§10.3 schema requires these).
    if (!ndaFirst.trim() || !ndaLast.trim() || !ndaAddr1.trim() || !ndaTown.trim() || !ndaPostcode.trim() || !ndaCountry.trim()) {
      setNdaError(true);
      return;
    }
    setNdaError(false);
    setNdaGenError(false);
    setSigning(true);
    // BFF: POST /api/v1/agreements → NdaPdfService (Playwright server-side).
    api
      .post<{ ok: boolean; agreementId: string; reference: string; dateLabel: string; signedAt: string }>('/api/v1/agreements', {
        firstName: ndaFirst.trim(),
        lastName: ndaLast.trim(),
        addr1: ndaAddr1.trim(),
        addr2: ndaAddr2.trim() || undefined,
        town: ndaTown.trim(),
        county: ndaCounty.trim() || undefined,
        postcode: ndaPostcode.trim(),
        country: ndaCountry,
      })
      .then((res) => {
        setSigning(false);
        setConfStatus('inplace');
        setShowNdaForm(false);
        session.setConfidentiality(true);
        if (res.agreementId) setAgreementId(res.agreementId);
        // Build the agreement object from the form values + BFF response so the
        // pop-up can render the document inline without an extra fetch. Uses the
        // server-generated dateLabel + signedAt for consistency with the PDF.
        const disclosee: DiscloseeDetails = {
          firstName: ndaFirst.trim(),
          lastName: ndaLast.trim(),
          addr1: ndaAddr1.trim(),
          addr2: ndaAddr2.trim() || undefined,
          town: ndaTown.trim(),
          county: ndaCounty.trim() || undefined,
          postcode: ndaPostcode.trim(),
          country: ndaCountry,
        };
        setNdaAgreement({
          disclosee,
          reference: res.reference,
          dateLabel: res.dateLabel,
          signedAt: res.signedAt,
        });
        // Auto-open the pop-up with the "Agreement created" banner (matches prototype).
        setNdaModalIsNew(true);
        setNdaModalOpen(true);
      })
      .catch(() => {
        setSigning(false);
        setNdaGenError(true); // generation failed (not a validation error)
        // Keep the form open so the user can retry. Don't pretend success.
      });
  };

  const addFiles = (fileList: FileList) => {
    const ideaId = session.activeIdeaId;
    if (!ideaId) return;
    const toAdd = Array.from(fileList).slice(0, 5);
    // Enforce the per-idea 5-file limit client-side (the BFF enforces too).
    if (files.length + toAdd.length > 5) {
      showToast('You can upload a maximum of 5 files per idea.');
      return;
    }
    for (const file of toAdd) {
      const tempId = `${Date.now()}-${Math.random()}`;
      setFiles((prev) => [...prev, { id: tempId, name: file.name, size: file.size, uploading: true }]);
      // Upload the file to the BFF (raw binary + X-Filename header). The
      // Firebase ID token is attached as a Bearer header.
      (async () => {
        try {
          const { auth } = await import('../lib/firebase.js');
          const { getIdToken } = await import('firebase/auth');
          const user = auth.currentUser;
          const token = user ? await getIdToken(user) : null;
          const arrayBuffer = await file.arrayBuffer();
          const res = await fetch(`/api/v1/uploads/${ideaId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/octet-stream',
              'X-Filename': file.name,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: arrayBuffer,
          });
          if (!res.ok) throw new Error('Upload failed');
          const data = await res.json();
          setFiles((prev) =>
            prev.map((f) => (f.id === tempId ? { ...f, id: data.id, uploading: false } : f)),
          );
        } catch {
          setFiles((prev) => prev.map((f) => (f.id === tempId ? { ...f, uploading: false, error: true } : f)));
          showToast(`Couldn't upload ${file.name}`);
        }
      })();
    }
  };

  const removeFile = (id: string) => {
    const ideaId = session.activeIdeaId;
    // Delete from the BFF (best-effort — ignore failures).
    (async () => {
      try {
        const { auth } = await import('../lib/firebase.js');
        const { getIdToken } = await import('firebase/auth');
        const user = auth.currentUser;
        const token = user ? await getIdToken(user) : null;
        await fetch(`/api/v1/uploads/${ideaId}/${id}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch {
        // Non-fatal.
      }
    })();
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const confirmBooking = () => {
    // BFF: POST /api/v1/bookings — coach match + calendar insert + Firestore.
    // The day may come from bookDays (the static next-6-weekdays list) OR from
    // daySlots (the BFF-fetched slots, which include today when same-day
    // booking is enabled — §9.4). Prefer the BFF day when available so the
    // same-day case (today, not in bookDays) works.
    const bffDay = daySlots?.find((d) => d.iso === selectedDay);
    const day = bffDay ? new Date(bffDay.date) : bookDays.find((d) => isoOf(d) === selectedDay);
    if (!day || !selectedSlot) return;

    // Flush any pending idea edits before booking so the idea record is current.
    saveIdeaNow();

    // Resolve the slot start/end. When real availability slots were fetched
    // from the BFF, selectedSlot is the ISO start time + the end is on the slot
    // object. Otherwise (static fallback) selectedSlot is a label we parse.
    const realDay = daySlots?.find((d) => d.iso === selectedDay);
    const realSlot = realDay?.slots.find((s) => s.start === selectedSlot);
    const callDuration = 30; // minutes — matches CALENDAR_DEFAULT_CALL_DURATION_MIN
    const start = realSlot ? new Date(realSlot.start) : slotLabelToDate(day, selectedSlot);
    const startIso = start.toISOString();
    const endIso = realSlot ? realSlot.end : new Date(start.getTime() + callDuration * 60 * 1000).toISOString();
    const slotLabelStr = realSlot ? realSlot.label : selectedSlot;

    api
      .post<{
        ok: boolean;
        bookingId: string;
        coach: { name: string; role: string } | null;
        slotLabel: string;
      }>('/api/v1/bookings', {
        ideaName: ideaName,
        category,
        slotStart: startIso,
        slotEnd: endIso,
        slotLabel: `${longDate(day)}, ${slotLabelStr}`,
        phone,
        customer: ideaCustomer,
        problem: ideaProblem,
        solution: ideaSolves,
      }, undefined, devToolHeaders(devTools))
      .then((res) => {
        // §12 conversion goal page — on the user's FIRST booking, redirect to
        // /booking-confirmed so Google Ads registers the goal. The goal page
        // marks the celebration flag (once-per-user). Subsequent bookings land
        // on the inline confirmation view as before.
        const wasFirstBooking = !session.celebrations.booking;
        setBooked(true);
        setBookFailed(false);
        session.setBooking({
          bookingId: res.bookingId,
          coachName: res.coach?.name ?? matchedCoach?.name ?? 'Your innovation coach',
          coachRole: res.coach?.role ?? matchedCoach?.role ?? 'Innovation coach',
          slotLabel: res.slotLabel,
          status: 'confirmed',
        });
        session.markCelebration('booking');
        if (wasFirstBooking) {
          navigate('/booking-confirmed', { replace: true });
        }
      })
      .catch(() => {
        // Booking failed — show an apology + a phone fallback. Don't pretend
        // the booking succeeded. The BFF also emails the admin on failure.
        setBookFailed(true);
      });
  };

  const cancelBooking = () => {
    // Show a confirmation dialog first — cancelling deletes the calendar event
    // + notifies ops, so it shouldn't happen on an accidental click.
    setShowCancelConfirm(true);
  };

  const confirmCancelBooking = () => {
    const bookingId = session.booking?.bookingId;
    setCancelling(true);
    // Call the BFF cancel route so the Firestore booking is marked cancelled,
    // the calendar event is deleted, + the ops email is sent. If no bookingId
    // (e.g. the booking was never persisted), just clear the local state.
    if (bookingId) {
      api
        .post(`/api/v1/bookings/${bookingId}/cancel`)
        .then(() => {
          setCancelling(false);
          setShowCancelConfirm(false);
          setBooked(false);
          session.setBooking(null);
          setSelectedDay(null);
          setSelectedSlot(null);
        })
        .catch(() => {
          // Still clear locally — the booking may already be gone server-side.
          setCancelling(false);
          setShowCancelConfirm(false);
          setBooked(false);
          session.setBooking(null);
          setSelectedDay(null);
          setSelectedSlot(null);
        });
    } else {
      setCancelling(false);
      setShowCancelConfirm(false);
      setBooked(false);
      session.setBooking(null);
      setSelectedDay(null);
      setSelectedSlot(null);
    }
  };

  // Reschedule the booking to a new slot (same coach — continuity pinning).
  const rescheduleBooking = () => {
    const bookingId = session.booking?.bookingId;
    if (!bookingId || !selectedDay || !selectedSlot) return;
    // §9.4 — the day may be today (same-day), which isn't in bookDays. Prefer
    // the BFF-fetched day when available.
    const bffDay = daySlots?.find((d) => d.iso === selectedDay);
    const day = bffDay ? new Date(bffDay.date) : bookDays.find((d) => isoOf(d) === selectedDay);
    if (!day) return;
    // Resolve the slot start/end. When real availability slots were fetched,
    // selectedSlot is the ISO start; otherwise parse the label.
    const realDay = daySlots?.find((d) => d.iso === selectedDay);
    const realSlot = realDay?.slots.find((s) => s.start === selectedSlot);
    const callDuration = 30; // minutes — matches CALENDAR_DEFAULT_CALL_DURATION_MIN
    const start = realSlot ? new Date(realSlot.start) : slotLabelToDate(day, selectedSlot);
    const startIso = start.toISOString();
    const endIso = realSlot ? realSlot.end : new Date(start.getTime() + callDuration * 60 * 1000).toISOString();
    const slotLabelStr = realSlot ? realSlot.label : selectedSlot;
    const newSlotLabel = `${longDate(day)}, ${slotLabelStr}`;
    api
      .post(`/api/v1/bookings/${bookingId}/reschedule`, {
        slotStart: startIso,
        slotEnd: endIso,
        slotLabel: newSlotLabel,
      })
      .then(() => {
        session.setBooking({
          bookingId,
          coachName: session.booking?.coachName ?? matchedCoach?.name ?? 'Your innovation coach',
          coachRole: session.booking?.coachRole ?? matchedCoach?.role ?? 'Innovation coach',
          slotLabel: newSlotLabel,
          status: 'rescheduled',
        });
        setShowReschedule(false);
        setSelectedDay(null);
        setSelectedSlot(null);
        showToast('Your review call has been rescheduled');
      })
      .catch(() => {
        showToast("We couldn't reschedule — please try again");
        setSelectedDay(null);
        setSelectedSlot(null);
      });
  };

  // Send the latest idea details to the coach (updates the calendar event
  // description). Best-effort — does not re-commit to Zoho.
  const sendUpdatesToCoach = () => {
    const bookingId = session.booking?.bookingId;
    if (!bookingId) return;
    setSendingUpdates(true);
    saveIdeaNow();
    api
      .post(`/api/v1/bookings/${bookingId}/update-details`, {
        ideaName,
        description: [ideaCustomer, ideaProblem, ideaSolves].filter(Boolean).join(' — '),
      })
      .then(() => {
        setSendingUpdates(false);
        showToast('Your latest idea details have been sent to your coach');
      })
      .catch(() => {
        setSendingUpdates(false);
        showToast("We couldn't send your updates — please try again");
      });
  };

  const summaryText = useMemo(() => {
    if (!selectedDay || !selectedSlot) return '';
    // §9.4 — the day may be today (same-day), which isn't in bookDays.
    const bffDay = daySlots?.find((d) => d.iso === selectedDay);
    const day = bffDay ? new Date(bffDay.date) : bookDays.find((d) => isoOf(d) === selectedDay);
    if (!day) return '';
    // When real slots are in use, selectedSlot is the ISO start; find its label.
    const realDay = daySlots?.find((d) => d.iso === selectedDay);
    const realSlot = realDay?.slots.find((s) => s.start === selectedSlot);
    return `${longDate(day)} at ${realSlot?.label ?? selectedSlot}`;
  }, [selectedDay, selectedSlot, bookDays, daySlots]);

  if (bookFailed) {
    return (
      <>
        <TopNav title="Book Idea Review" onBack={() => navigate('/')} onTitleTap={onTitleTap} />
        <div className="itk-wrap itk-wrap--book">
          <section className="itk-section-box itk-confirm">
            <div className="itk-confirm-icon" style={{ background: 'rgba(217, 67, 67, 0.1)', color: '#d94343' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="itk-overline">Sorry</div>
            <h2 className="itk-sec-title">Booking isn’t available right now</h2>
            <p className="itk-confirm-desc">
              We couldn’t complete your booking at the moment. This is usually temporary. Please try again
              in a few minutes, or if you’d prefer to speak to us straight away:
            </p>
            <div className="itk-confirm-actions" style={{ marginTop: 16 }}>
              <Button pill onClick={() => { setBookFailed(false); }}>
                Try again
              </Button>
              <a href="tel:+441179289000" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 12, color: 'var(--ink)', fontWeight: 600 }}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Call us on 0117 928 9000
              </a>
            </div>
          </section>
        </div>
      </>
    );
  }

  if (booked) {
    const bookingCoach = session.booking?.coachName ?? matchedCoach?.name ?? 'Your innovation coach';
    return (
      <>
        <TopNav title="Book Idea Review" onBack={() => navigate('/')} onTitleTap={onTitleTap} />
        <div className="itk-wrap itk-wrap--book">
          <section className="itk-section-box itk-coach-box itk-confirm">
            <div className="itk-confirm-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="itk-overline">
              {session.booking?.status === 'rescheduled' ? 'Rescheduled' : 'Confirmed'}
            </div>
            <h2 className="itk-sec-title">Idea review booked</h2>
            <p className="itk-confirm-desc">Your idea review has been booked.</p>

            <div className="itk-bk-details">
              <div className="itk-bk-row">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span className="itk-bk-cell">
                  <span className="itk-bk-k">When</span>
                  <span className="itk-bk-v">{session.booking?.slotLabel || summaryText || '—'}</span>
                </span>
              </div>
              <div className="itk-bk-row">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                  <circle cx="10" cy="7" r="4" />
                </svg>
                <span className="itk-bk-cell">
                  <span className="itk-bk-k">Your coach</span>
                  <span className="itk-bk-v">{bookingCoach}</span>
                </span>
              </div>
              <div className="itk-bk-row">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className="itk-bk-cell">
                  <span className="itk-bk-k">We&rsquo;ll call you on</span>
                  <span className="itk-bk-v">{phone || '—'}</span>
                </span>
              </div>
            </div>

            {/* Confidentiality agreement — show View if in place. */}
            {confStatus === 'inplace' && agreementId && (
              <div className="itk-status-box itk-status-box--ok" style={{ marginTop: 16 }}>
                <span className="itk-status-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2l4-4" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </span>
                <div className="itk-status-body">
                  <div className="itk-status-title">Confidentiality Agreement in place</div>
                  <span className="itk-status-text">Your agreement is signed and on file.</span>
                  <div className="itk-status-actions">
                    <Button size="sm" pill variant="secondary" onClick={viewAgreement}>View agreement</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Idea details — editable even after booking. Edits autosave to
                the idea doc; use "Send updates to coach" to push them to the
                calendar event. */}
            <div className="itk-bk-idea-edit" style={{ marginTop: 20 }}>
              <div className="itk-overline">Your idea</div>
              <h3 className="itk-sec-title" style={{ fontSize: 16, marginBottom: 12 }}>Idea details</h3>
              <Input
                label="Idea name"
                value={ideaName}
                onChange={(e) => setIdeaName(e.target.value)}
                onBlur={saveIdeaNow}
                placeholder="What is your idea called?"
              />
              <div style={{ marginTop: 12 }}>
                <Input
                  label="Who is it for? (your ideal customer)"
                  value={ideaCustomer}
                  onChange={(e) => setIdeaCustomer(e.target.value)}
                  onBlur={saveIdeaNow}
                  placeholder="e.g. Cat owners who travel frequently"
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <Input
                  label="What problem does it solve?"
                  value={ideaProblem}
                  onChange={(e) => setIdeaProblem(e.target.value)}
                  onBlur={saveIdeaNow}
                  placeholder="e.g. They worry about leaving their cat alone"
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <Input
                  label="How does it solve it?"
                  value={ideaSolves}
                  onChange={(e) => setIdeaSolves(e.target.value)}
                  onBlur={saveIdeaNow}
                  placeholder="e.g. An automated feeder with a camera"
                />
              </div>
              <p className="itk-sec-desc" style={{ fontSize: 13, marginTop: 10, marginBottom: 0 }}>
                Changes save automatically. Use &ldquo;Send updates to coach&rdquo; below to update your
                coach&rsquo;s calendar invite with your latest details.
              </p>
            </div>

            {/* Uploaded documents — viewable + deletable after booking. */}
            <div className="itk-bk-uploads" style={{ marginTop: 20 }}>
              <div className="itk-overline">Documents</div>
              <h3 className="itk-sec-title" style={{ fontSize: 16, marginBottom: 12 }}>Your uploaded files</h3>
              {files.length === 0 && (
                <p className="itk-sec-desc" style={{ fontSize: 13, marginBottom: 12 }}>
                  No files uploaded yet. You can add drawings, photos or documents about your idea below.
                </p>
              )}
              <label className="itk-dropzone" style={{ display: 'block', cursor: 'pointer', marginBottom: files.length > 0 ? 12 : 0 }}>
                <input
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => e.target.files && addFiles(e.target.files)}
                />
                <div className="itk-dropzone-title" style={{ fontSize: 14 }}>Click to upload files (or drag &amp; drop)</div>
                <div className="itk-dropzone-sub" style={{ fontSize: 12 }}>PDF, JPG, PNG or DOCX — up to 25 MB each, 5 files max</div>
              </label>
              {files.map((f) => (
                <div key={f.id} className="itk-file-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid rgba(17,22,35,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} style={{ flexShrink: 0 }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.name} {f.uploading && <span style={{ color: 'var(--text-3)' }}>(uploading…)</span>}
                      {f.error && <span style={{ color: 'var(--danger)' }}>(failed)</span>}
                    </span>
                  </div>
                  {!f.uploading && !f.error && (
                    <button
                      type="button"
                      onClick={() => removeFile(f.id)}
                      aria-label={`Remove ${f.name}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            <p className="itk-confirm-note">
              A calendar reminder is on its way to your inbox. Please email{' '}
              <a href="mailto:info@innovate-design.co.uk">info@innovate-design.co.uk</a> if you are unable to
              attend.
            </p>

            {/* Reschedule slot picker (shown when the user clicks Reschedule). */}
            {showReschedule && (
              <div className="itk-reschedule-picker" style={{ marginTop: 16 }}>
                <div className="itk-overline">Reschedule</div>
                <h3 className="itk-sec-title">Pick a new time</h3>
                <p className="itk-sec-desc">Your coach will stay the same.</p>
                <div className="itk-slot-grid">
                  {(daySlots ?? bookDays.map((d) => ({ date: d.toISOString(), iso: isoOf(d), dayLabel: longDate(d), slots: [], tag: undefined as 'limited' | 'none' | undefined }))).map((day) => (
                    <button
                      key={day.iso}
                      type="button"
                      className={`itk-day-chip${selectedDay === day.iso ? ' itk-day-chip--active' : ''}${day.tag === 'none' ? ' itk-day-chip--disabled' : ''}`}
                      disabled={day.tag === 'none'}
                      onClick={() => setSelectedDay(day.iso)}
                    >
                      {day.dayLabel ?? longDate(new Date(day.date))}
                      {day.tag === 'limited' && ' (limited)'}
                      {day.tag === 'none' && ' (none)'}
                    </button>
                  ))}
                </div>
                {selectedDay && (
                  <div className="itk-slot-grid" style={{ marginTop: 12 }}>
                    {(() => {
                      const realDay = daySlots?.find((d) => d.iso === selectedDay);
                      const list = realDay?.slots.map((s) => ({ key: s.start, label: s.label })) ?? ['9.00 am', '10.30 am', '12.00 pm', '1.15 pm', '2.45 pm', '4.15 pm'].map((s) => ({ key: s, label: s }));
                      if (list.length === 0) return <span style={{ color: 'var(--text-3)', fontSize: 14 }}>No slots available this day.</span>;
                      return list.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          className={`itk-day-chip${selectedSlot === s.key ? ' itk-day-chip--active' : ''}`}
                          onClick={() => setSelectedSlot(s.key)}
                        >
                          {s.label}
                        </button>
                      ));
                    })()}
                  </div>
                )}
                <div className="itk-confirm-actions" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                  <Button pill className="itk-btn-on-turq" onClick={rescheduleBooking} disabled={!selectedDay || !selectedSlot}>
                    Confirm new time
                  </Button>
                  <Button variant="secondary" pill className="itk-btn-on-turq" onClick={() => setShowReschedule(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="itk-confirm-actions" style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              <Button pill onClick={() => navigate('/alerts')}>
                Go to Alert Centre
                <ArrowIcon />
              </Button>
              <Button variant="secondary" pill onClick={() => navigate('/')} className="itk-btn-on-turq">
                Idea Toolkit home
              </Button>
              {!showReschedule && (
                <Button variant="secondary" pill className="itk-btn-on-turq" onClick={() => {
                  setSelectedDay(null);
                  setSelectedSlot(null);
                  setShowReschedule(true);
                  // Fetch fresh slots for the booked coach from the BFF (the
                  // post-booking view doesn't run matchCoach, so daySlots is null
                  // — without this fetch the picker falls back to static
                  // placeholder slots). Best-effort; falls back to the static
                  // grid if the fetch fails.
                  const coachName = session.booking?.coachName;
                  if (coachName) {
                    api
                      .get<{ days: { date: string; iso: string; dayLabel: string; slots: { start: string; end: string; label: string }[]; tag?: 'limited' | 'none' }[] }>(`/api/v1/bookings/slots?coachName=${encodeURIComponent(coachName)}`)
                      .then((res) => { if (res.days?.length) setDaySlots(res.days); })
                      .catch(() => { /* Non-fatal — static grid is the fallback. */ });
                  }
                }}>
                  Reschedule
                </Button>
              )}
              <Button variant="secondary" pill className="itk-btn-on-turq" onClick={sendUpdatesToCoach} loading={sendingUpdates}>
                Send updates to coach
              </Button>
              <button type="button" className="itk-bk-cancel" onClick={cancelBooking}>
                Cancel this booking
              </button>
            </div>

            {/* Cancel confirmation dialog — cancelling deletes the calendar
                event + notifies ops, so require an explicit confirmation. */}
            {showCancelConfirm && (
              <div className="itk-confirm-dialog" role="dialog" aria-label="Cancel booking confirmation">
                <div className="itk-confirm-dialog__inner">
                  <h3 className="itk-confirm-dialog__title">Cancel this booking?</h3>
                  <p className="itk-confirm-dialog__desc">
                    This will cancel your review call, remove the calendar event, and notify our team. This
                    cannot be undone. You can book a new call afterwards.
                  </p>
                  <div className="itk-confirm-dialog__actions">
                    <Button pill onClick={() => setShowCancelConfirm(false)} disabled={cancelling} className="itk-btn-on-turq">
                      Keep my booking
                    </Button>
                    <Button variant="danger" pill onClick={confirmCancelBooking} loading={cancelling}>
                      Yes, cancel my booking
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        {toast && (
          <div className="itk-toast itk-toast--show" role="status" aria-live="polite">
            {toast}
          </div>
        )}
        {showDevPanel && (
          <DevPanel
            state={devTools}
            onChange={updateDevTools}
            onClose={closeDevPanel}
            bookingId={session.booking?.bookingId}
          />
        )}
        <NdaModal
          open={ndaModalOpen}
          isNew={ndaModalIsNew}
          email={session.email ?? null}
          disclosee={ndaAgreement?.disclosee ?? null}
          dateLabel={ndaAgreement?.dateLabel ?? ''}
          reference={ndaAgreement?.reference ?? ''}
          signedAt={ndaAgreement?.signedAt ?? ''}
          onClose={() => setNdaModalOpen(false)}
          onDownload={downloadAgreement}
          onResend={resendAgreement}
        />
      </>
    );
  }

  return (
    <>
      <TopNav title="Book Idea Review" onBack={() => navigate('/')} onTitleTap={onTitleTap} />
      <div className="itk-wrap itk-wrap--book">
        <div className="itk-page-head">
          <div className="itk-overline">Free expert review</div>
          <h1 className="itk-page-title">Book Idea Review</h1>
          <p className="itk-sec-desc">
            Book a free, confidential idea review with a UK innovation coach and discuss your options protect
            and profit from your idea.
          </p>
        </div>

        <section className="itk-section-box">
          <div className="itk-overline">Your innovation coach</div>
          <h2 className="itk-sec-title">About your idea review call</h2>
          <div className="itk-about-card">
            <p>
              This is your chance to confidentially review your idea with an innovation coach who has years of
              experience helping inventors like you to commercialise their ideas.
            </p>
            <p>
              All our innovation coaches are based in the UK and work for Innovate Design. Innovate Design
              has been helping inventors for over 25 years, and recently set up and delivered the Product
              Design module for the UK&rsquo;s no.1 Innovation Masters degree course at Warwick Business
              School.
            </p>
          </div>
          <h3 className="itk-card-title">Match with an innovation coach</h3>
          <p className="itk-sec-desc">
            To help us match you with the best innovation coach, tell us which category your idea most closely
            matches.
          </p>
          <div className="itk-match-row">
            <div className="itk-field" style={{ marginBottom: 0 }}>
              <label className="itk-field-label" htmlFor="itk-inCategory">Idea category</label>
              <div className="itk-select-wrap">
                <select
                  className="itk-input"
                  id="itk-inCategory"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Select a category&hellip;</option>
                  {BOOK_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <Button pill onClick={matchCoach} loading={matching} disabled={!category}>
              Match with a coach
            </Button>
          </div>
          {coachMatched && (
            <div className="itk-coach-card itk-coach-card--show" aria-live="polite">
              <div className="itk-coach-avatar">
                <span>IC</span>
                <span className="itk-coach-dot" />
              </div>
              <div>
                <div className="itk-coach-name">{matchedCoach?.name ?? 'Your innovation coach'}</div>
                <div className="itk-coach-role">{matchedCoach?.role ?? 'Innovation coach'}</div>
                <p className="itk-coach-bio">
                  {matchedCoach?.bio ?? 'Your coach will be confirmed at booking. We match you to the best available coach based on your idea category and current workload.'}
                </p>
              </div>
            </div>
          )}
        </section>

        <div role="region" aria-label="Confidentiality status" style={{ marginBottom: 20 }}>
          {confStatus === 'none' && (
            <div className="itk-status-box itk-status-box--info">
              <span className="itk-status-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </span>
              <div className="itk-status-body">
                <div className="itk-status-title">Before your call, get a Confidentiality Agreement in place</div>
                <span className="itk-status-text">
                  Ask us to legally agree to keep your idea confidential.
                </span>
                {!showNdaForm && (
                  <div className="itk-status-actions">
                    <Button size="sm" pill onClick={requestNDA}>Request confidentiality agreement</Button>
                  </div>
                )}
                {showNdaForm && (
                  <div className="itk-nda-form">
                    <p className="itk-nda-intro">
                      Completing this form will create a one-way agreement based on a UK Government template.
                      We will legally agree to keep your idea confidential. You will not be committed in any
                      way and you keep full control of your idea. Once you have added your details we will
                      email you the signed agreement and add a copy here.
                    </p>
                    <div className="itk-party-fields">
                      <div className="itk-field-row itk-field-row--pair">
                        <input className="itk-input" placeholder="First name(s)" autoComplete="given-name" value={ndaFirst} onChange={(e) => setNdaFirst(e.target.value)} />
                        <input className="itk-input" placeholder="Last name" autoComplete="family-name" value={ndaLast} onChange={(e) => setNdaLast(e.target.value)} />
                      </div>
                      <div className="itk-field-row">
                        <input className="itk-input" placeholder="Address line 1" autoComplete="address-line1" value={ndaAddr1} onChange={(e) => setNdaAddr1(e.target.value)} />
                      </div>
                      <div className="itk-field-row">
                        <input className="itk-input" placeholder="Address line 2 (optional)" autoComplete="address-line2" value={ndaAddr2} onChange={(e) => setNdaAddr2(e.target.value)} />
                      </div>
                      <div className="itk-field-row itk-field-row--pair">
                        <input className="itk-input" placeholder="Town / City" autoComplete="address-level2" value={ndaTown} onChange={(e) => setNdaTown(e.target.value)} />
                        <input className="itk-input" placeholder="County (optional)" autoComplete="address-level1" value={ndaCounty} onChange={(e) => setNdaCounty(e.target.value)} />
                      </div>
                      <div className="itk-field-row itk-field-row--pair">
                        <input className="itk-input" placeholder="Postcode" autoComplete="postal-code" style={{ textTransform: 'uppercase' }} value={ndaPostcode} onChange={(e) => setNdaPostcode(e.target.value)} />
                        <div className="itk-select-wrap">
                          <select className="itk-input" value={ndaCountry} onChange={(e) => setNdaCountry(e.target.value)}>
                            <option>United Kingdom</option>
                            <option>Ireland</option>
                            <option>France</option>
                            <option>Germany</option>
                            <option>Spain</option>
                            <option>Italy</option>
                            <option>Netherlands</option>
                            <option>United States</option>
                            <option>Canada</option>
                            <option>Australia</option>
                            <option>New Zealand</option>
                            <option>Other</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    {ndaError && (
                      <p className="itk-field-err itk-field-err--show" style={{ marginTop: 8 }}>
                        Please fill in your name, address line 1, town, postcode and country.
                      </p>
                    )}
                    {ndaGenError && (
                      <p className="itk-field-err itk-field-err--show" style={{ marginTop: 8 }}>
                        The agreement couldn’t be generated right now — please try again in a moment.
                      </p>
                    )}
                    <Button size="sm" pill onClick={signNDA} loading={signing}>
                      Request Digitally Signed Agreement
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          {confStatus === 'inplace' && (
            <div className="itk-status-box itk-status-box--good">
              <span className="itk-status-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <div className="itk-status-body">
                <div className="itk-status-title">Confidentiality agreement in place</div>
                <span className="itk-status-text">
                  We have a signed confidentiality agreement in place, so you can share your idea with
                  confidence.
                </span>
                <div className="itk-status-actions">
                  <button type="button" className="itk-link-inline" style={{ color: 'var(--success)' }} onClick={viewAgreement} disabled={!agreementId}>
                    View agreement
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <section className="itk-section-box">
          <div className="itk-overline">Idea details</div>
          <h2 className="itk-sec-title">Tell us about your idea</h2>
          <p className="itk-sec-desc">
            All your coach needs to get started is a name for your idea. Anything else you add below is
            optional, but the more your coach knows ahead of the call, the more helpful the conversation will
            be.
          </p>
          <div className="itk-field">
            <label className="itk-field-label" htmlFor="itk-inName">
              Idea name <span className="itk-req">*</span>
            </label>
            <input className="itk-input" id="itk-inName" autoComplete="off" placeholder="Give your idea a working name" value={ideaName} onChange={(e) => { setIdeaName(e.target.value); markIdeaDirty(); }} onBlur={saveIdeaNow} />
          </div>
          <div className="itk-field">
            <label className="itk-field-label" htmlFor="itk-inCustomer">Who is the customer for your idea?</label>
            <div className="itk-field-hint">Optional</div>
            <textarea className="itk-input itk-textarea" id="itk-inCustomer" placeholder="Describe the person or business most likely to buy it" value={ideaCustomer} onChange={(e) => { setIdeaCustomer(e.target.value); markIdeaDirty(); }} onBlur={saveIdeaNow} />
          </div>
          <div className="itk-field">
            <label className="itk-field-label" htmlFor="itk-inProblem">What problem does your idea solve?</label>
            <div className="itk-field-hint">Optional</div>
            <textarea className="itk-input itk-textarea" id="itk-inProblem" placeholder="Describe the problem or frustration your idea addresses" value={ideaProblem} onChange={(e) => { setIdeaProblem(e.target.value); markIdeaDirty(); }} onBlur={saveIdeaNow} />
          </div>
          <div className="itk-field">
            <label className="itk-field-label" htmlFor="itk-inSolves">How does your idea solve the problem?</label>
            <div className="itk-field-hint">Optional</div>
            <textarea className="itk-input itk-textarea" id="itk-inSolves" placeholder="Describe how it works and why it's better than the alternatives" value={ideaSolves} onChange={(e) => { setIdeaSolves(e.target.value); markIdeaDirty(); }} onBlur={saveIdeaNow} />
          </div>

          <div className="itk-field" style={{ marginTop: 22 }}>
            <label className="itk-field-label">Upload documents</label>
            <div className="itk-field-hint">Upload photos, sketches or other documents to give your coach more detail. Optional.</div>
            <label
              className="itk-dropzone"
              tabIndex={0}
              role="button"
              aria-label="Upload documents"
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('itk-dropzone--drag');
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('itk-dropzone--drag');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('itk-dropzone--drag');
                if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div className="itk-dropzone-title">Drag &amp; drop files here, or click to browse</div>
              <div className="itk-dropzone-sub">PDF, JPG, PNG or DOCX — up to 25 MB each</div>
              <input type="file" multiple style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => e.target.files && addFiles(e.target.files)} />
            </label>
            {files.length > 0 && (
              <div className="itk-file-list">
                {files.map((f) => (
                  <div className="itk-file-item" key={f.id}>
                    <svg className="itk-fi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="itk-file-name">{f.name}</span>
                    <span className="itk-file-size">{fmtBytes(f.size)}</span>
                    <button type="button" className="itk-file-del" onClick={() => removeFile(f.id)} aria-label={`Remove ${f.name}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="itk-section-box">
          <div className="itk-overline">Book your call</div>
          <h2 className="itk-sec-title">Choose a time that suits you</h2>
          <p className="itk-sec-desc">Pick a call-back slot and give us a number to reach you on. Your coach will call you at the time you choose.</p>
          <div className="itk-field" style={{ maxWidth: 360 }}>
            <label className="itk-field-label" htmlFor="itk-inPhone">Phone number we can call you on <span className="itk-req">*</span></label>
            <input className="itk-input" id="itk-inPhone" inputMode="tel" autoComplete="tel" placeholder="e.g. 07700 900123" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className={`itk-book-zone ${detailsComplete ? 'itk-book-zone--ready' : ''}`}>
            {!detailsComplete && (
              <div className="itk-book-overlay">
                <div className="itk-book-overlay-inner">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <div className="itk-book-overlay-text">Give your idea a name above to book your call.</div>
                </div>
              </div>
            )}
            <div className="itk-slots-label">Select a day</div>
            <div className="itk-day-scroll">
              {(daySlots ?? bookDays.map((d) => ({ date: d.toISOString(), iso: isoOf(d), dayLabel: longDate(d), slots: [], tag: undefined as 'limited' | 'none' | undefined }))).map((day) => {
                const iso = day.iso;
                const tag = day.tag;
                return (
                  <button
                    key={iso}
                    className={`itk-day-chip ${selectedDay === iso ? 'on' : ''} ${tag === 'none' ? 'itk-day-chip--none' : ''}`}
                    disabled={tag === 'none'}
                    onClick={() => setSelectedDay(iso)}
                  >
                    <span className="itk-dow">{DOW[new Date(day.date).getDay()]}</span>
                    <span className="itk-dnum">{new Date(day.date).getDate()}</span>
                    <span className="itk-dmon">{MON[new Date(day.date).getMonth()]}</span>
                    {tag === 'limited' && <span className="itk-day-tag">Limited</span>}
                    {tag === 'none' && <span className="itk-day-tag">None</span>}
                  </button>
                );
              })}
            </div>
            <div className="itk-slots-label">Select a time</div>
            <div className="itk-slot-grid">
              {slotsLoading && <span className="itk-slots-loading">Checking availability…</span>}
              {!slotsLoading && (() => {
                // Use real slots from the BFF if available; otherwise fall back
                // to the static SLOTS list (pre-calendar-setup behaviour).
                const realDay = daySlots?.find((d) => d.iso === selectedDay);
                const list = realDay?.slots.map((s) => ({ key: s.start, label: s.label })) ?? SLOTS.map((s) => ({ key: s, label: s }));
                if (list.length === 0) return <span className="itk-slots-empty">No slots available this day.</span>;
                return list.map((s) => (
                  <button
                    key={s.key}
                    className={`itk-slot ${selectedSlot === s.key ? 'on' : ''}`}
                    disabled={!selectedDay}
                    onClick={() => setSelectedSlot(s.key)}
                  >
                    {s.label}
                  </button>
                ));
              })()}
            </div>
            <div className="itk-book-summary">
              {summaryText && (
                <div className="itk-book-summary-row">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span>{summaryText}</span>
                </div>
              )}
              <Button pill onClick={confirmBooking} disabled={!bookReady}>Book call</Button>
            </div>
          </div>
        </section>

        <div className="itk-back-top-wrap">
          <Button variant="secondary" pill onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
            Back to the top
          </Button>
        </div>
      </div>
      {showDevPanel && (
        <DevPanel
          state={devTools}
          onChange={updateDevTools}
          onClose={closeDevPanel}
          bookingId={session.booking?.bookingId}
        />
      )}
      <NdaModal
        open={ndaModalOpen}
        isNew={ndaModalIsNew}
        email={session.email ?? null}
        disclosee={ndaAgreement?.disclosee ?? null}
        dateLabel={ndaAgreement?.dateLabel ?? ''}
        reference={ndaAgreement?.reference ?? ''}
        signedAt={ndaAgreement?.signedAt ?? ''}
        onClose={() => setNdaModalOpen(false)}
        onDownload={downloadAgreement}
        onResend={resendAgreement}
      />
    </>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}